import React from 'react';
import TitleHelmet from 'components/TitleHelmet';
import { Link } from 'react-router-dom';

const FindingConcentration: React.FC = () => {
  return (
    <div className="finding-concentration-page">
      <TitleHelmet title="How to Find Your Medication Concentration" />

      <div className="content-section">
        <section className="introduction">
          <h2>Finding Your Tirzepatide Concentration</h2>
          <p>
            Medication labels can look confusing with all their numbers and text, but there's one key piece of information you need: <strong>the concentration</strong>.
          </p>
          <p>
            Think of concentration like the strength of your medication. Just like a strong cup of coffee has more caffeine per ounce than weak coffee, a higher concentration medication has more medicine packed into each milliliter of liquid.
          </p>
          <p>
            <strong>What you're looking for:</strong> A number followed by "mg/mL" (like 10mg/mL or 20mg/mL). This tells you how many milligrams of medicine are in each milliliter of liquid.
          </p>
          <p>
            <strong>Simple example:</strong> If your label says "15mg/mL", that means every 1mL (which equals 100 units on your syringe) contains 15mg of medicine. So for a 15mg dose, you'd inject exactly 1mL (100 units).
          </p>
        </section>
        <div className="disclaimer-box">
          <h3>⚠️ Important Warning</h3>
          This guide is for educational purposes only and is not medical advice. Always verify your medication's
          concentration with your healthcare provider or pharmacist.
        </div>
        <section id="what-is-concentration" className="key-terms">
          <h2>What is Concentration?</h2>

          <div className="concentration-explainer">
            <h3>Think of it Like Coffee</h3>

            <p>Think about different coffee drinks and their caffeine concentration:</p>
            <ul>
              <li>
                An espresso shot is highly concentrated - lots of caffeine in a tiny amount of liquid (like a 20 mg/mL
                medication)
              </li>
              <li>
                An Americano uses that same espresso shot but adds water - same amount of caffeine spread out in more
                liquid (like a 10 mg/mL medication)
              </li>
              <li>
                To get the same amount of caffeine, you'd need to drink twice as much Americano as espresso - just like
                you might need twice the volume of a 10 mg/mL medication compared to a 20 mg/mL medication to get the
                same dose
              </li>
            </ul>
            <img
              src="/images/conc_coffee.webp"
              alt="Concentration Explainer"
              style={{
                width: 'clamp(200px, 80vw, 600px)',
                height: 'auto',
                display: 'block',
                margin: '0 auto',
              }}
            />
            <p>
              Medication concentration works exactly the same way - it's how much medicine is packed into each
              milliliter (mL) of liquid. Just like you need to know if you're drinking espresso or Americano to
              understand your caffeine intake, you need to know your medication's concentration to calculate the right
              dose.
            </p>
          </div>

          <div className="term-grid">
            <div className="term-card">
              <h3 style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>Concentration (mg/mL)</h3>
              <p style={{ fontSize: '0.8rem', margin: '0' }}>The strength per mL needed for calculator, e.g. 10mg/mL</p>
            </div>
            <div className="term-card">
              <h3 style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>Total Volume</h3>
              <p style={{ fontSize: '0.8rem', margin: '0' }}>Amount of liquid in vial, e.g. 3mL</p>
            </div>
            <div className="term-card">
              <h3 style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>Total Content</h3>
              <p style={{ fontSize: '0.8rem', margin: '0' }}>
                Total medication in vial, which equals Concentration × Volume, e.g. 30mg
              </p>
            </div>
          </div>
        </section>

        <section id="understanding-your-label" className="label-examples">
          <h2>Understanding your Label</h2>

          <div className="label-format">
            <h3>Standard Format</h3>
            <p>Most common format showing concentration as X mg/mL:</p>
            <div className="label-example">
              <pre>
                {`COMPOUNDED MEDICATION
`}
                <span className="highlight">Tirzepatide 20 mg/mL</span>
                {`
For subcutaneous injection only
Qty: 5mL       Rx#: 12345
Exp: 03/24/2025
Store refrigerated (2-8°C/36-46°F)
Example Pharmacy, LLC
123 Main St, City, ST 12345
(555) 555-5555`}
              </pre>
            </div>
            <p className="label-explanation">
              In this format, the concentration is clearly stated as mg per mL. This is the easiest format to read as no
              calculation is needed.
            </p>
            <div className="concentration-highlight">
              <strong>The concentration is 20 mg/mL, so you would enter "20" in the calculator.</strong>
            </div>
          </div>

          <div className="label-format">
            <h3>Partial mL Format</h3>
            <p>Sometimes pharmacies list concentration per 0.5mL instead of per full mL:</p>
            <div className="label-example">
              <pre>
                {`COMPOUNDED MEDICATION
`}
                <span className="highlight">Tirzepatide 5mg/0.5mL</span>
                {`
Total Volume: 2.5mL
For subcutaneous injection only
Rx#: 67890       Lot#: TZ0224A
Exp: 04/15/2025
Keep Refrigerated
Pharmacy Name Compounding
456 Oak Ave, City, ST 12345
(555) 555-5556`}
              </pre>
            </div>
            <p className="label-explanation">
              <strong>Quick math needed:</strong> When you see "0.5mL" (which is half of 1mL), double the first number to get the concentration per full mL.
            </p>
            <p>
              <strong>Step by step:</strong>
            </p>
            <ul>
              <li>Label shows: 5mg/0.5mL</li>
              <li>Since 0.5mL = half of 1mL...</li>
              <li>Double the 5mg: 5mg × 2 = 10mg</li>
              <li><strong>Answer: 10mg/mL</strong></li>
            </ul>
            <div className="concentration-highlight">
              <strong>The concentration is 10 mg/mL, so you would enter "10" in the calculator.</strong>
            </div>
          </div>

          <div className="label-format">
            <h3>Format with Additives</h3>
            <p>Many pharmacies add extra ingredients like B12, niacinamide, or glycine:</p>
            <div className="label-example">
              <pre>
                {`COMPOUNDED MEDICATION
`}
                <span className="highlight">Tirzepatide</span>/<span className="additive">Niacinamide</span>
                {` `}
                <span className="highlight">8</span>/<span className="additive">2</span>
                {` mg/mL
Volume: 5mL       Rx#: 13579
Exp: 03/30/2025   Lot#: TZN0224B
Store in refrigerator
Discard after: 28 days from first use
Professional Compounding Pharmacy
789 Elm St, City, ST 12345
(555) 555-5557`}
              </pre>
            </div>
            <p className="label-explanation">
              <strong>Key rule:</strong> Only the tirzepatide concentration matters for dosing. Ignore all the other ingredients and their numbers.
            </p>
            <p>
              <strong>How to find the right number:</strong>
            </p>
            <ul>
              <li>Look for "Tirzepatide" in the ingredient list</li>
              <li>Find which position it's in (first, second, etc.)</li>
              <li>Use the concentration number in that same position</li>
              <li>In this example: Tirzepatide is listed first, so use the first number (8 mg/mL)</li>
            </ul>
            <div className="concentration-highlight">
              <strong>The tirzepatide concentration is 8 mg/mL, so you would enter "8" in the calculator.</strong>
              <br />
              <em>(Ignore the niacinamide and its concentration—it doesn't affect your dosing)</em>
            </div>
          </div>
        </section>

        <section id="common-pitfalls" className="common-pitfalls">
          <h2>Common Mistakes to Avoid</h2>
          <ul>
            <li>
              <strong>Don't confuse "total content" with concentration</strong>
              <p>
                <strong>Wrong:</strong> "My vial says 60mg, so the concentration is 60mg/mL"
              </p>
              <p>
                <strong>Right:</strong> A "60mg vial" tells you the total amount of medicine, not the concentration. You need to know how much liquid that 60mg is dissolved in. For example:
              </p>
              <ul>
                <li>60mg dissolved in 3mL = 20mg/mL concentration</li>
                <li>60mg dissolved in 4mL = 15mg/mL concentration</li>
              </ul>
            </li>
            <li>
              <strong>Watch out for concentrations shown in partial mLs (like 0.5mL)</strong>
              <p>
                <strong>Wrong:</strong> "5mg/0.5mL means 5mg/mL"
              </p>
              <p>
                <strong>Right:</strong> Always convert to full mL. Since 0.5mL is half of 1mL, double the amount: 5mg × 2 = 10mg/mL
              </p>
            </li>
            <li>
              <strong>Focus only on tirzepatide or semaglutide when there are multiple ingredients</strong>
              <p>
                <strong>Problem:</strong> Labels with "Tirzepatide/B12: 10/500 mg/mL" can be confusing
              </p>
              <p>
                <strong>Solution:</strong> Only use the tirzepatide or semaglutide number (10mg/mL). Ignore B12, niacinamide, glycine, and other additives completely.
              </p>
            </li>
            <li>
              <strong>Don't mix up mg and mcg</strong>
              <p>
                <strong>Watch out for:</strong> Some labels show additives in micrograms (mcg) or international units (IU)
              </p>
              <p>
                <strong>Remember:</strong> Tirzepatide and semaglutide are always measured in milligrams (mg). If you see "500mcg" on your label, that's probably an additive, not your tirzepatide concentration.
              </p>
            </li>
          </ul>
        </section>

        {/* Quiz Promo Section */}
        <section className="quiz-promo-section">
          <h3>Ready to Test Your Knowledge?</h3>
          <p>
            You've learned how to find medication concentration on different types of labels. Now, put your skills to the test with our interactive
            label reading quiz!
          </p>
          <Link to="/quiz/concentration-label" className="quiz-promo-button">
            🧠 Take the Label Concentration Quiz
          </Link>
        </section>
      </div>

      <style>{`
        .finding-concentration-page {
          padding: 2rem 0;
        }

        .content-section {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 1rem;
        }

        .disclaimer-box {
          background-color: #fff3f3;
          border: 2px solid #ff0000;
          border-radius: 8px;
          padding: 1.5rem;
          margin: 2rem 0;
        }

        .disclaimer-box h3 {
          color: #ff0000;
          margin-top: 0;
        }

        .term-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 0.75rem;
          margin: 1rem 0;
        }

        .term-card {
          background-color: #f5f5f5;
          padding: 0.75rem;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .term-card h3 {
          margin-top: 0;
          color: #252441;
        }

        .label-format {
          margin: 2rem 0;
        }

        .label-example {
          background-color: white;
          padding: 1.5rem;
          border-radius: 8px;
          border: 1px solid #ddd;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          font-family: monospace;
          white-space: pre-wrap;
          margin: 1rem 0;
        }

        .label-example pre {
          margin: 0;
          white-space: pre-wrap;
          font-family: monospace;
          font-size: 0.9rem;
        }

        .label-example .highlight {
          background-color: #fff3b0;
          padding: 2px 4px;
          border-radius: 2px;
        }

        .label-example .additive {
          background-color: #ffebeb;
          color: #cc0000;
          padding: 2px 4px;
          border-radius: 2px;
          margin-left: 4px;
        }

        .label-explanation {
          background-color: #f5f5f5;
          padding: 1rem;
          border-radius: 4px;
          margin-top: 1rem;
          font-style: italic;
        }

        .common-pitfalls ul {
          list-style-type: none;
          padding-left: 0;
        }

        .common-pitfalls li {
          margin: 1rem 0;
          padding-left: 2rem;
          position: relative;
        }

        .common-pitfalls li:before {
          content: "⚠️";
          position: absolute;
          left: 0;
        }

        @media (max-width: 600px) {
          .label-example pre {
            font-size: 0.8rem;
          }
        }

        @media (max-width: 749px) {
          .term-grid {
             grid-template-columns: 1fr;
          }
        }

        .concentration-highlight {
          background-color: #e6f3ff;
          border-left: 4px solid #0066cc;
          padding: 1rem;
          margin: 1rem 0;
          border-radius: 0 4px 4px 0;
        }

        .concentration-highlight strong {
          color: #0066cc;
          font-size: 1.1rem;
        }

        .concentration-highlight em {
          display: block;
          margin-top: 0.5rem;
          font-size: 0.9rem;
          color: #666;
        }

        .content-section section {
          scroll-margin-top: 80px; /* Adjust this value based on your header height */
        }

        /* Styles for the Quiz Promo Section */
        .quiz-promo-section {
          background-color: #eef4ff; /* A light blue background */
          border: 2px solid #0066cc;   /* A darker blue border */
          border-radius: 8px;
          padding: 1.5rem;
          margin: 2rem 0; /* Spacing above and below */
          text-align: center;
        }

        .quiz-promo-section h3 {
          color: #004c99; /* Darker blue for the heading */
          margin-top: 0;
          margin-bottom: 0.75rem;
        }

        .quiz-promo-section p {
          margin-bottom: 1.25rem;
          font-size: 1rem;
          color: #333;
        }

        .quiz-promo-button {
          display: inline-block;
          background-color: #0066cc; /* Primary button color */
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: 5px;
          text-decoration: none;
          font-weight: bold;
          font-size: 1rem;
          transition: background-color 0.2s ease;
        }

        .quiz-promo-button:hover {
          background-color: #0052a3; /* Darker shade on hover */
          color: white;
        }
      `}</style>
    </div>
  );
};

export default FindingConcentration;
